import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Badge,
  ButtonGroup,
  Alert,
  ProgressBar
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { addMachine, setMachines } from "../redux/DataSlice";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import CommonModal from "components/ui/CommonModal";
import ActionButtons from "components/ui/ActionButtons";
import AddButton from "components/ui/AddButton";
import { GRADIENTS } from "utils/theme";
import usePagination from "hooks/usePagination";
import Pagination from "components/ui/Pagination";
import TableFilter from "components/ui/TableFilter";
import { useSearchParams, useNavigate } from "react-router-dom";
import { showDeleteConfirmation, showSuccessAlert, showErrorAlert } from "utils/swalAlert";
import "./machines.css";

function Machines() {
  const dispatch = useDispatch();
  const { fetchData } = Useaxios();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [machineStats,setMachineStats]=useState({
    online: 0,
    offline: 0,
    total: 0
  })

  const getInitialFilters = () => {
    const status = searchParams.get('status');
    return {
      name: '',
      location: '',
      status: status || ''
    };
  };

  const fetchMachines = useCallback(async (params) => {
    const res = await fetchData({
      url: API.getMachine,
      method: 'GET',
      params,
      showMessage: false
    });
    console.log(res)
    if(res.success){
      setMachineStats(res.data.globalCounts)
    }
    return res;
  }, []);
 

  const {
    data: machines,
    loading,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    filters,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    updateFilters,
    clearFilters,
    refresh
  } = usePagination(fetchMachines, getInitialFilters());

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    machineCode: '',
    machineName: '',
    location: '',
    status: 'offline'
  });
  const [editingMachine, setEditingMachine] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.machineCode.trim()) {
      newErrors.machineCode = "Machine code is required";
    }
    if (formData.machineCode.length < 3) {
      newErrors.machineCode = "Machine code must be at least 3 characters";
    }
    if (!formData.machineName.trim()) {
      newErrors.machineName = "Machine name is required";
    }
    if (formData.machineName.length < 3) {
      newErrors.machineName = "Machine name must be at least 3 characters";
    }
    if (!formData.location.trim()) {
      newErrors.location = "Location is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);
    try {
      if (editingMachine) {
        const updatedMachine = { ...editingMachine, ...formData };
        const res = await fetchData({
          url: `${API.updateMachine}/${editingMachine._id}`,
          method: "PUT",
          data: updatedMachine
        });
        if (res.success) {
          refresh();
          resetForm();
        }
      } else {
        const newMachine = {
          name: formData.machineName,
          location: formData.location,
          status: formData.status,
          machineId: formData.machineCode
        };

        const res = await fetchData({
          url: API.createMachine,
          method: "POST",
          data: newMachine
        });

        if (res.success) {
          refresh();
          resetForm();
        }
      }

    } catch (error) {
      console.error("Error saving machine:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (machine) => {
    setEditingMachine(machine);

    setFormData({
      machineCode: machine.machineId || '',
      machineName: machine.name || '',
      location: machine.location || '',
      status: machine.status || 'offline',
    });

    setErrors({});
    setShowModal(true);
  };


  const handleDelete = (machine) => {
    showDeleteConfirmation({
      title: 'Delete Machine?',
      text: 'You are about to delete machine',
      itemName: machine.name,
      onConfirm: async () => {
        try {
          const res = await fetchData({
            url: `${API.deleteMachine}/${machine._id}`,
            method: "DELETE"
          });
          if (res.success) {
            refresh();
            showSuccessAlert('Machine has been deleted successfully.');
          }
        } catch (error) {
          showErrorAlert('Failed to delete machine.');
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({ machineCode: '', machineName: '', location: '', status: 'offline' });
    setEditingMachine(null);
    setShowModal(false);
    setErrors({});
  };

  const getStatusStats = () => {
    const online = (machines && Array.isArray(machines)) && machines.filter(m => m.status === 'online').length || 0;
    const offline = (machines && Array.isArray(machines)) && machines.filter(m => m.status === 'offline').length || 0;
    return { online, offline, total: machines?.length || 0 };
  };

  const statusStats = getStatusStats();

  useEffect(() => {
    const status = searchParams.get('status');
    if (status && (status === 'online' || status === 'offline')) {
      updateFilters({ status });
      setShowFilters(true);
    }
  }, [searchParams]);

  const filterFields = [
    {
      key: 'name',
      label: 'Machine Name',
      type: 'text',
      placeholder: 'Search by name...'
    },
    {
      key: 'machineCode',
      label: 'Machine Code',
      type: 'text',
      placeholder: 'Search by Machine Code...'
    },
    {
      key: 'location',
      label: 'Location',
      type: 'text',
      placeholder: 'Search by location...'
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        // { value: '', label: 'All Status' },
        { value: 'online', label: 'Online' },
        { value: 'offline', label: 'Offline' }
      ]
    }
  ];

  return (
    <>
      <Container fluid className="py-4">
        <Row>
          <Col md="12">
            <Card className="shadow border-0 rounded-3 overflow-hidden">
              <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-0 fw-semibold" style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                      <i className="fas fa-robot me-2"></i>
                      <span className="d-none d-sm-inline">Machines Management</span>
                      <span className="d-inline d-sm-none">Machines</span>
                    </h4>
                    <p className="mb-0 opacity-75 mt-1 d-none d-md-block" style={{ fontSize: '0.9rem' }}>
                      Monitor and manage all your vending machines
                    </p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="fw-medium d-flex align-items-center"
                    >
                      <i className="fas fa-filter me-1"></i>
                      <span className="d-none d-md-inline">Filters</span>
                      {Object.values(filters).filter(v => v).length > 0 && (
                        <Badge bg="danger" className="ms-2">{Object.values(filters).filter(v => v).length}</Badge>
                      )}
                    </Button>
                    <AddButton
                      onClick={() => {
                        resetForm();
                        setShowModal(true);
                      }}
                      icon="fas fa-plus-circle"
                      variant="light"
                      className="fw-medium"
                    >
                      <span className="d-none d-md-inline">Add New Machine</span>
                      <span className="d-inline d-md-none">Add</span>
                    </AddButton>
                  </div>
                </div>
              </div>

              {showFilters && (
                <div className="px-4 pt-3 border-bottom">
                  <TableFilter
                    filterConfig={filterFields}
                    filters={filters}
                    onFilterChange={updateFilters}
                    onClearFilters={clearFilters}
                  />
                </div>
              )}

              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading machines...</p>
                  </div>
                ) : machines.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="display-1 text-muted mb-3 opacity-50">
                      <i className="fas fa-robot"></i>
                    </div>
                    <h4 className="text-muted mb-3">No Machines Found</h4>
                    <p className="text-muted mb-4">Get started by adding your first vending machine</p>
                    <Button
                      variant="primary"
                      onClick={() => {
                        resetForm();
                        setShowModal(true);
                      }}
                      className="px-4 fw-medium"
                    >
                      <i className="fas fa-plus-circle me-2"></i>Add Machine
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Statistics Cards */}
                    <div className="p-4 bg-light border-bottom">
                      <Row className="g-3">
                        <Col md={4}>
                          <div className="stat-card">
                            <div className="d-flex align-items-center">
                              <div className="stat-icon bg-primary bg-opacity-10 text-primary rounded-circle p-3 me-3">
                                <i className="fas fa-robot fa-lg"></i>
                              </div>
                              <div>
                                <h3 className="fw-bold mb-0">{machineStats.total}</h3>
                                <small className="text-muted">Total Machines</small>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="stat-card">
                            <div className="d-flex align-items-center">
                              <div className="stat-icon bg-success bg-opacity-10 text-success rounded-circle p-3 me-3">
                                <i className="fas fa-signal fa-lg"></i>
                              </div>
                              <div>
                                <h3 className="fw-bold mb-0">{machineStats.online}</h3>
                                <small className="text-muted">Online Now</small>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="stat-card">
                            <div className="d-flex align-items-center">
                              <div className="stat-icon bg-danger bg-opacity-10 text-danger rounded-circle p-3 me-3">
                                <i className="fas fa-exclamation-circle fa-lg"></i>
                              </div>
                              <div>
                                <h3 className="fw-bold mb-0">{machineStats.offline}</h3>
                                <small className="text-muted">Offline</small>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Machines Table */}
                    <div className="table-responsive">
                      <Table hover className="mb-0 align-middle machines-table">
                        <thead className="bg-light">
                          <tr>
                            <th className="border-0 ps-4">Machine Code</th>
                            <th className="border-0">Name</th>
                            <th className="border-0">Location</th>
                            <th className="border-0 text-center">Status</th>
                            <th className="border-0 text-center">Health</th>
                            {/* <th className="border-0 text-center">Slots</th>
                            <th className="border-0 text-center">Layers</th> */}

                            <th className="border-0 text-end pe-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {machines.map((machine) => {
                            return (
                              <tr key={machine._id} className="align-middle">
                                <td className="ps-4">
                                  <div className="d-flex align-items-center">
                                    <div className="machine-icon bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-3">
                                      <i className="fas fa-robot"></i>
                                    </div>
                                    <div>
                                      <h6 className="mb-0 fw-semibold">{machine.machineId}</h6>
                                      {/* <small className="text-muted">ID: {machine._id?.substring(0, 8)}...</small> */}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <h6 className="mb-0 fw-semibold">{machine.name}</h6>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <i className="fas fa-map-marker-alt text-muted me-2"></i>
                                    <span className="text-dark">{machine.location}</span>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Badge
                                    bg={machine.status === 'online' ? 'success' : 'danger'}
                                    className="rounded-pill px-3 py-2"
                                    style={{ fontSize: '0.85rem' }}
                                  >
                                    <i className={`fas fa-circle me-1 ${machine.status === 'online' ? 'text-white' : 'text-light'}`}
                                      style={{ fontSize: '0.5rem' }}>
                                    </i>
                                    {machine.status || 'offline'}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex flex-column align-items-center">
                                    <ProgressBar
                                      now={machine.status === 'online' ? 100 : 0}
                                      variant={machine.status === 'online' ? 'success' : 'danger'}
                                      className="w-75"
                                      style={{ height: '6px' }} />
                                    <small className="text-muted mt-1">
                                      {machine.status === 'online' ? 'Healthy' : 'Down'}
                                    </small>
                                  </div>
                                </td>
{/* 
                                <td>
                                  <Button onClick={() => navigate(`/admin/machines/slots/${machine.machineId}`)}>{machine.totalSlots ? machine.totalSlots : "➕"} </Button>
                                </td>
                                <td>
                                  <Button onClick={() => navigate(`/admin/machines/layers/${machine.machineId}`)}>{machine.totalLayers ? machine.totalLayers : "➕"} </Button>
                                </td> */}
                                <td className="text-end pe-4 listing-actions-cell">
                                  <ActionButtons
                                    onEdit={() => handleEdit(machine)}
                                    onDelete={() => handleDelete(machine)}

                                    // onView={() => navigate(`/admin/machines/${machine.machineId}`)}
                                    onView={() => navigate(`/admin/machines/layers/${machine.machineId}`)}
                                    showView={true}
                                    size="sm"
                                    variant="outline" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </Card.Body>

              {machines.length > 0 && (
                <Card.Footer className="bg-light border-0 py-3 px-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={goToPage}
                    onNextPage={nextPage}
                    onPrevPage={prevPage}
                    onPageSizeChange={changePageSize}
                  />
                </Card.Footer>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Machine Modal */}
      <CommonModal
        visible={showModal}
        onClose={resetForm}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className={`fas ${editingMachine ? 'fa-edit' : 'fa-plus-circle'} fa-lg`}></i>
            </div>
            <div>
              <h5 className="mb-0 fw-semibold">{editingMachine ? 'Edit Machine' : 'Add New Machine'}</h5>
              <small className="opacity-75">
                {editingMachine ? 'Update machine details' : 'Register a new vending machine'}
              </small>
            </div>
          </div>
        }
        size="lg"
        confirmColor="light"
        onConfirm={handleSubmit}
        confirmText={editingMachine ? 'Update' : 'Create'}
        cancelText="Cancel"
        showConfirm={true}
        showCancel={true}
        loading={loading}
        backdrop="static"
        showFooter={false}
        customHeaderClass="modal-header-gradient"
      >
        <div className="p-4">
          <Form onSubmit={handleSubmit}>
            {Object.keys(errors).length > 0 && (
              <Alert variant="danger" className="rounded-3 border-0 shadow-sm mb-4">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-triangle me-3 fs-4"></i>
                  <div>
                    <h6 className="mb-1 fw-semibold">Validation Error</h6>
                    <ul className="mb-0">
                      {Object.values(errors).map((error, index) => (
                        <li key={index} className="small">{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Alert>
            )}

            <div className="card border-0 bg-light rounded-3 p-4 mb-4">
              <h6 className="fw-semibold text-dark mb-4">
                <i className="fas fa-info-circle text-primary me-2"></i>
                Basic Information
              </h6>

              <Form.Group className="mb-4">
                <Form.Label className="fw-medium mb-2">
                  <i className="fas fa-barcode text-primary me-2"></i>
                  Machine Code <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                  </span>
                  <Form.Control
                    type="text"
                    placeholder="e.g., VS-001, MACH-001"
                    value={formData.machineCode}
                    onChange={(e) => {
                      setFormData({ ...formData, machineCode: e.target.value });
                      if (errors.machineCode) setErrors({ ...errors, machineCode: '' });
                    }}
                    isInvalid={!!errors.machineCode}
                    className="form-control-custom border-start-0"
                  />
                </div>
                {errors.machineCode && (
                  <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-2">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {errors.machineCode}
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-muted mt-2">
                  Enter a unique code for your vending machine
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-medium mb-2">
                  <i className="fas fa-robot text-primary me-2"></i>
                  Machine Name <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                  </span>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Airport Vending Machine, Mall Counter"
                    value={formData.machineName}
                    onChange={(e) => {
                      setFormData({ ...formData, machineName: e.target.value });
                      if (errors.machineName) setErrors({ ...errors, machineName: '' });
                    }}
                    isInvalid={!!errors.machineName}
                    className="form-control-custom border-start-0"
                  />
                </div>
                {errors.machineName && (
                  <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-2">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {errors.machineName}
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-muted mt-2">
                  Enter a descriptive name for your vending machine
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-medium mb-2">
                  <i className="fas fa-map-marker-alt text-primary me-2"></i>
                  Location <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="fas fa-location-dot text-muted"></i>
                  </span>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Mumbai Airport Terminal 1, Main Lobby"
                    value={formData.location}
                    onChange={(e) => {
                      setFormData({ ...formData, location: e.target.value });
                      if (errors.location) setErrors({ ...errors, location: '' });
                    }}
                    isInvalid={!!errors.location}
                    className="form-control-custom border-start-0"
                  />
                </div>
                {errors.location && (
                  <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-2">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {errors.location}
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-muted mt-2">
                  Specify the exact location for this machine
                </Form.Text>
              </Form.Group>
            </div>
            {editingMachine && <div className="card border-0 bg-light rounded-3 p-4 mb-4">
              <h6 className="fw-semibold text-dark mb-4">
                <i className="fas fa-power-off text-primary me-2"></i>
                Machine Status
              </h6>

              <div className="text-center">
                <div className="d-flex justify-content-center mb-3">
                  <ButtonGroup className="shadow-sm rounded-3 overflow-hidden">
                    <Button
                      variant={formData.status === 'offline' ? 'danger' : 'outline-danger'}
                      onClick={() => setFormData({ ...formData, status: 'offline' })}
                      className="px-4 py-2 fw-medium border-0"
                      style={{
                        minWidth: '120px',
                        borderTopLeftRadius: '8px !important',
                        borderBottomLeftRadius: '8px !important'
                      }}
                    >
                      <i className="fas fa-power-off me-2"></i>
                      Offline
                    </Button>
                    <Button
                      variant={formData.status === 'online' ? 'success' : 'outline-success'}
                      onClick={() => setFormData({ ...formData, status: 'online' })}
                      className="px-4 py-2 fw-medium border-0"
                      style={{
                        minWidth: '120px',
                        borderTopRightRadius: '8px !important',
                        borderBottomRightRadius: '8px !important'
                      }}
                    >
                      <i className="fas fa-wifi me-2"></i>
                      Online
                    </Button>
                  </ButtonGroup>
                </div>

                <div className="status-indicator mt-3">
                  {formData.status === 'online' ? (
                    <div className="d-flex align-items-center justify-content-center text-success">
                      <i className="fas fa-check-circle me-2"></i>
                      <span className="fw-medium">Machine will be marked as operational</span>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center text-danger">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      <span className="fw-medium">Machine will be marked as offline</span>
                    </div>
                  )}
                </div>
              </div>
            </div>}


            {/* Machine ID Preview */}
            {/* {!editingMachine && (
              <div className="card border-0 bg-light rounded-3 p-4 mb-4">
                <h6 className="fw-semibold text-dark mb-3">
                  <i className="fas fa-id-card text-primary me-2"></i>
                  Machine ID (Auto-generated)
                </h6>
                <div className="bg-white rounded-3 p-3 text-center">
                  <div className="text-muted small mb-2">Preview of generated ID:</div>
                  <div className="fw-bold text-dark fs-5">
                    MACH-{Date.now().toString().slice(-6)}
                  </div>
                  <div className="text-muted small mt-2">
                    This ID will be automatically assigned upon creation
                  </div>
                </div>
              </div>
            )} */}

            {/* Modal Footer */}
            <div className="modal-footer-custom mt-4 pt-3">
              <div className="d-flex gap-3 justify-content-end">
                <Button
                  variant="outline-secondary"
                  onClick={resetForm}
                  className="btn-action"
                  disabled={submitLoading}
                >
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  className="btn-action"
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {editingMachine ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <i className={`fas ${editingMachine ? 'fa-save' : 'fa-plus-circle'} me-2`}></i>
                      {editingMachine ? 'Update Machine' : 'Create Machine'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </CommonModal>
    </>
  );
}

export default Machines;
